import { z } from 'zod';
import { DomainError } from '../errors.js';
import { parseLocalDate } from '../local-date.js';
import { toIssues } from '../materials/catalog.js';
import { isValidRunningHours } from '../running-hours.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Dolaşan (rotable) bileşen ve kendi çalışma saati (DNV-CP-0206 Sec.2 [4.3] c; ADR 0133 §6):
 * seri numaralı parça (ör. yakıt enjektörü, turboşarj kartuşu) bir konumdan (ekipman) sökülüp
 * revizyondan sonra başka konuma takılabilir. Saat parçanın kendisinde birikir: kayıttaki ilk saat
 * + her takılı kaldığı dönemde konum sayacının ilerlediği saat. Takma ve sökme değiştirilemez
 * hareket kaydıdır; saat okuma anında hareketlerden hesaplanır (saklanmaz).
 */
export const ROTABLE_MOVEMENT_KINDS = ['install', 'remove'] as const;
export type RotableMovementKind = (typeof ROTABLE_MOVEMENT_KINDS)[number];

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const text = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const localDate = z.string(typed('INVALID_DATE')).refine((value) => {
  try {
    parseLocalDate(value);
    return true;
  } catch {
    return false;
  }
}, 'INVALID_DATE');
/** Saat: 0 ya da pozitif, en çok bir ondalık. */
const hours = () =>
  z
    .number(typed('INVALID_NUMBER'))
    .min(0, 'OUT_OF_RANGE')
    .max(1_000_000, 'OUT_OF_RANGE')
    .refine((v) => v === 0 || isValidRunningHours(v), 'INVALID_HOURS');

export const rotableInputSchema = z.strictObject(
  {
    serialNo: text(64),
    name: text(200),
    partNo: optionalText(64),
    maker: optionalText(120),
    /** Kayıt anındaki saat (ör. son revizyondan beri); bilinmiyorsa 0. */
    initialHours: hours()
      .nullish()
      .transform((v) => v ?? 0),
    registeredOn: localDate,
    notes: optionalText(2000),
  },
  { error: () => 'INVALID_OBJECT' },
);
export type RotableInput = z.output<typeof rotableInputSchema>;

export const rotableMovementInputSchema = z.strictObject(
  {
    kind: z.enum(ROTABLE_MOVEMENT_KINDS, typed('INVALID_OPTION')),
    /** Konum: takıldığı ya da söküldüğü ekipman. */
    equipmentId: z.uuid(typed('INVALID_ID')),
    movedOn: localDate,
    /** Konum sayacının o andaki toplamı; sayaçsız konumda `null` (o dönem saat birikmez). */
    positionHours: hours()
      .nullish()
      .transform((v) => v ?? null),
    reason: optionalText(500),
  },
  { error: () => 'INVALID_OBJECT' },
);
export type RotableMovementInput = z.output<typeof rotableMovementInputSchema>;

export function parseRotableInput(
  input: unknown,
  options: { today?: string } = {},
): ParseResult<RotableInput> {
  const result = rotableInputSchema.safeParse(input);
  if (!result.success) return { ok: false, issues: toIssues(result.error) };
  if (options.today !== undefined && result.data.registeredOn > options.today) {
    return { ok: false, issues: [{ path: 'registeredOn', code: 'FUTURE_DATE' }] };
  }
  return { ok: true, value: result.data };
}

export function parseRotableMovementInput(
  input: unknown,
  options: { today?: string } = {},
): ParseResult<RotableMovementInput> {
  const result = rotableMovementInputSchema.safeParse(input);
  if (!result.success) return { ok: false, issues: toIssues(result.error) };
  if (options.today !== undefined && result.data.movedOn > options.today) {
    return { ok: false, issues: [{ path: 'movedOn', code: 'FUTURE_DATE' }] };
  }
  return { ok: true, value: result.data };
}

export interface RotableMovement {
  kind: RotableMovementKind;
  equipmentId: string;
  movedOn: string;
  positionHours: number | null;
}

/** Hareketin yerleşik sırası: gün, sonra kayıt sırası (çağıran kayıt sırasıyla verir). */
export interface RotablePlacement {
  /** Şu an takılı olduğu konum; depoda/atölyede `null`. */
  equipmentId: string | null;
  since: string | null;
  sinceHours: number | null;
}

export function rotablePlacement(movements: readonly RotableMovement[]): RotablePlacement {
  const last = movements.at(-1);
  if (!last || last.kind === 'remove') return { equipmentId: null, since: null, sinceHours: null };
  return { equipmentId: last.equipmentId, since: last.movedOn, sinceHours: last.positionHours };
}

/**
 * Yeni hareketin geçerliliği: takılı parça yeniden takılmaz (`ROTABLE_ALREADY_INSTALLED`),
 * takılı olmayan sökülmez (`ROTABLE_NOT_INSTALLED`), söküm takıldığı konumdan
 * (`ROTABLE_POSITION_MISMATCH`), tarih son hareketten önce olamaz (`ROTABLE_DATE_ORDER`), sökümde
 * konum saati takıldığı andakinden az olamaz (`ROTABLE_HOURS_DECREASE`). Kayıttan önce takılmaz.
 */
export function assertRotableMovement(
  movements: readonly RotableMovement[],
  next: RotableMovement,
  registeredOn: string,
): void {
  const last = movements.at(-1);
  const placement = rotablePlacement(movements);
  if (next.movedOn < registeredOn || (last && next.movedOn < last.movedOn)) {
    throw new DomainError('ROTABLE_DATE_ORDER', next.movedOn);
  }
  if (next.kind === 'install') {
    if (placement.equipmentId !== null) {
      throw new DomainError('ROTABLE_ALREADY_INSTALLED', placement.equipmentId);
    }
    return;
  }
  if (placement.equipmentId === null) throw new DomainError('ROTABLE_NOT_INSTALLED');
  if (placement.equipmentId !== next.equipmentId) {
    throw new DomainError('ROTABLE_POSITION_MISMATCH', placement.equipmentId);
  }
  if (
    placement.sinceHours !== null &&
    next.positionHours !== null &&
    next.positionHours < placement.sinceHours
  ) {
    throw new DomainError('ROTABLE_HOURS_DECREASE', String(placement.sinceHours));
  }
}

export interface RotableHoursPeriod {
  equipmentId: string;
  installedOn: string;
  removedOn: string | null;
  installHours: number | null;
  removeHours: number | null;
  /** Bu dönemde biriken saat; konum sayacı yoksa ya da bilinmiyorsa `null`. */
  hours: number | null;
}

export interface RotableHours {
  /** Kayıttaki saat + dönemlerin toplamı. */
  total: number;
  periods: RotableHoursPeriod[];
  /** Saati bilinmeyen (sayaçsız konum) dönem var: toplam eksik olabilir. */
  incomplete: boolean;
}

const round1 = (value: number) => Math.round(value * 10) / 10;

/**
 * Parçanın kendi çalışma saati: kayıttaki saat + her takılı dönemde konum sayacının ilerlemesi.
 * Açık (hâlâ takılı) dönem konumun güncel sayacıyla (`currentHoursOf`) kapanır.
 */
export function rotableHours(
  initialHours: number,
  movements: readonly RotableMovement[],
  currentHoursOf: (equipmentId: string) => number | null,
): RotableHours {
  const periods: RotableHoursPeriod[] = [];
  let open: RotableMovement | null = null;
  for (const m of movements) {
    if (m.kind === 'install') {
      open = m;
      continue;
    }
    if (!open) continue;
    periods.push(periodOf(open, m.movedOn, m.positionHours));
    open = null;
  }
  if (open) periods.push(periodOf(open, null, currentHoursOf(open.equipmentId)));
  const total = periods.reduce((sum, p) => sum + (p.hours ?? 0), initialHours);
  return {
    total: round1(total),
    periods,
    incomplete: periods.some((p) => p.hours === null),
  };
}

function periodOf(
  install: RotableMovement,
  removedOn: string | null,
  endHours: number | null,
): RotableHoursPeriod {
  const hours =
    install.positionHours !== null && endHours !== null
      ? round1(Math.max(0, endHours - install.positionHours))
      : null;
  return {
    equipmentId: install.equipmentId,
    installedOn: install.movedOn,
    removedOn,
    installHours: install.positionHours,
    removeHours: removedOn === null ? null : endHours,
    hours,
  };
}
